import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';

import {HttpService} from '../http.service';
import {UnitBankAccountReqRes} from 'src/app/shared/models/gp/UnitBankAccountReqRes.model';
import {UnitBankAccountListItem} from 'src/app/shared/models/gp/UnitBankAccountListItem.model';
import {AbstractRestService} from 'src/app/shared/types/AbstractRestService';
import {UnitTransactionReqRes} from '../../shared/models/gp/UnitTransactionReqRes.model';
import {UnitBankAccountBalanceReqRes} from '../../shared/models/gp/UnitBankAccountBalanceReqRes.model';
import {UnitAchLimits} from '../../shared/models/unit-bank-account/UnitAchLimits.model';
import {UnitBankAccountSearchOptions} from 'src/app/shared/models/unit-bank-account/UnitBankAccountSearchOptions.model';
import {SearchOptionsResponse} from 'src/app/shared/models/SearchOptionsResponse.model';
import {UnitBankAccountTransactionsSearchOptionsRequest} from 'src/app/shared/models/unit-bank-account/UnitBankAccountTransactionsSearchOptionsRequest.model';
import {UnitAccountLimits} from 'src/app/shared/models/unit-bank-account/UnitAccountLimits.model';
import {UnitTransactionNotes} from 'src/app/shared/models/unit-transaction/UnitTransactionNotes.model';

@Injectable()
export abstract class UnitBankAccountDataService extends AbstractRestService<UnitBankAccountReqRes, UnitBankAccountListItem> {
  abstract readonly baseEndpoint$;

  constructor(http: HttpService) {
    super(http);
  }

  abstract getSearchOptionsToQuerystring(options: UnitBankAccountSearchOptions): string;

  abstract getListBasic(options: UnitBankAccountSearchOptions): Observable<SearchOptionsResponse<UnitBankAccountListItem>>;

  abstract getUnitBankAccounts(options: UnitBankAccountSearchOptions): Observable<SearchOptionsResponse<UnitBankAccountListItem>>;

  /** Allows the current user to approve the bank account.
   * If this user is the lst one to sign,
   * it will also create the bank account in Unit.
   */
  abstract approve(bankAccountId: number);

  /**
   * Get transactions list for the bank account
   * @param bankAccountId: UnitResourceId
   */
  abstract getTransactionsList(bankAccountId: number, options: UnitBankAccountTransactionsSearchOptionsRequest): Observable<UnitTransactionReqRes[]>;

  /**
   * Gets account's balance, total expenses and total income
   * @param bankAccountId Unit bank account id
   */
  abstract getBalanceAndTotalTransactions(bankAccountId: number): Observable<UnitBankAccountBalanceReqRes>;

  /**
   * Get statement pdf
   * @param statementId Unit bank account statement id
   * @param bankAccountId Unit bank account id
   */
  abstract getBankAccountStatement(statementId: string, bankAccountId: string);

  /**
   * Gets unit account types
   */
  abstract getUnitAccountTypes();

  /**
   * Get bank account ACH limits
   * @param bankAccountId Bank account id
   */
  abstract getBankAccountAchLimits(bankAccountId: number): Observable<UnitAccountLimits>;

  abstract getTransactionsSearchOptionsToQuerystring(options: UnitBankAccountTransactionsSearchOptionsRequest): string;

  abstract downloadFaqPdf(bankAccountId: number): Observable<any>;

  abstract generateVerificationLetter(bankAccountId: number, includeBalance: boolean): Observable<any>;

  abstract exportTransactions(fileType: 'excel' | 'csv', unitBankId: number, options: UnitBankAccountTransactionsSearchOptionsRequest): Observable<string>;

  abstract closeAccount(bankAccountId: number): Observable<boolean>;

  abstract createTransactionNotes(transactionNotes: UnitTransactionNotes): Observable<UnitTransactionNotes>;

  abstract updateTransactionNotes(transactionNotes: UnitTransactionNotes): Observable<UnitTransactionNotes>;

  abstract deleteTransactionNotes(transactionNoteId: number): Observable<void>;
}
